import { useState } from "react";
import { Link } from "react-router-dom";

import type { MessageTestChannelResult, MessageTestResult, TournamentMessage } from "../lib/api";
import { usePushSubscription } from "../pwa/usePushSubscription";
import PushToggle from "./pwa/PushToggle";

// «Enviármelo de prueba» (Eduardo 2026-10-02): la prueba sale por correo, app
// y WhatsApp (si está conectado) solo al admin; aquí se ve qué pasó en cada
// canal, p. ej. «Correo ✓ · App ✓ (revisa tu celular) · WhatsApp: apagado».

const CHANNEL_NAMES: Record<MessageTestChannelResult["channel"], string> = {
  email: "Correo",
  app: "App",
  whatsapp: "WhatsApp",
};

/** Motivo corto de un canal que no salió (el detalle largo viene del servidor). */
const REASON_SHORT: Record<string, string> = {
  no_email: "sin correo en tu cuenta",
  not_configured: "no configurado",
  whatsapp_off: "apagado",
  auto_off: "automáticos apagados",
  not_connected: "sin conectar",
  no_phone: "sin número en tu perfil",
  invalid_phone: "número no válido",
  not_allowlisted: "tu número no está autorizado para pruebas",
  send_failed: "no se pudo enviar",
};

export function channelSummary(item: MessageTestChannelResult): string {
  const name = CHANNEL_NAMES[item.channel] ?? item.channel;
  if (item.status === "sent") {
    if (item.channel === "app") {
      if (item.push?.status === "sent") return "App ✓ (revisa tu celular)";
      if (item.push?.status === "no_subscription") return "App ✓ (sin avisos al celular)";
      return "App ✓";
    }
    return `${name} ✓`;
  }
  const reason = REASON_SHORT[item.reason ?? ""] ?? (item.status === "failed" ? "no se pudo enviar" : "no salió");
  return `${name}: ${reason}`;
}

export function summarizeMessageTest(result: MessageTestResult): string {
  return result.channels.map(channelSummary).join(" · ");
}

/** ¿Algún canal falló (no «apagado» ni «sin número»: falló al enviar)? */
export function messageTestHasFailure(result: MessageTestResult): boolean {
  return result.channels.some((item) => item.status === "failed");
}

/** Línea chica bajo el botón: por dónde sale la prueba de ESTE mensaje. */
export function testChannelsHint(channels: TournamentMessage["channels"]): string {
  const parts: string[] = [];
  if (channels.includes("email")) parts.push("correo");
  parts.push("app");
  if (channels.includes("whatsapp")) parts.push("WhatsApp si está conectado");
  if (parts.length === 1) return "Solo por la app";
  const text = `${parts.slice(0, -1).join(", ")} y ${parts[parts.length - 1]}`;
  return text.charAt(0).toUpperCase() + text.slice(1);
}

/** «Enviármelo de prueba» con su línea chica de canales. En «Entrar como
 * admin» (superadmin dentro de la cuenta de un organizador) no se ofrece: la
 * prueba le llegaría al organizador y no a quien toca el botón. */
export function MessageTestButton({
  channels,
  busy,
  impersonating,
  onSend,
}: {
  channels: TournamentMessage["channels"];
  busy: boolean;
  impersonating: boolean;
  onSend: () => void;
}) {
  return (
    <span className="aviso-prueba-boton">
      <button
        type="button"
        className="btn btn--ghost btn--compact"
        onClick={onSend}
        disabled={busy || impersonating}
        title={
          impersonating
            ? "Estás viendo la cuenta de otro organizador: la prueba le llegaría a él"
            : "Te llega solo a ti, con datos de ejemplo, por los mismos canales que al jugador"
        }
      >
        {busy ? "Enviando…" : "Enviármelo de prueba"}
      </button>
      <small className="muted">
        {impersonating ? "No disponible en la vista como admin: pruébalo desde tu cuenta" : testChannelsHint(channels)}
      </small>
    </span>
  );
}

function mark(status: MessageTestChannelResult["status"]): string {
  if (status === "sent") return "✓";
  if (status === "failed") return "✕";
  return "–";
}

/** El servidor no tiene ningún celular del admin con avisos: según lo que
 * permita ESTE navegador, ofrece activarlos, explica por qué no se puede o
 * vuelve a registrar el dispositivo (el navegador cree que sí está activado
 * pero el servidor lo dio de baja). Al activarlos pide repetir la prueba. */
function TestPushHelp() {
  const { state, busy, error, enable } = usePushSubscription();
  const [requested, setRequested] = useState(false);

  if (state === "loading") return null;
  if (state === "blocked" || state === "ios-needs-install") return <PushToggle />;
  if (state === "unsupported") {
    return (
      <p className="aviso-prueba-resultado__nota">
        Este navegador no puede recibir avisos al celular. Abre Torneos123 desde la app instalada en tu celular (o
        desde Chrome) y actívalos en «Mis notificaciones».
      </p>
    );
  }
  if (requested && !busy && !error && state === "on") {
    return (
      <p className="aviso-prueba-resultado__nota" role="status">
        Listo: avisos activados en este dispositivo. Vuelve a tocar «Enviármelo de prueba» para recibir la prueba aquí.
      </p>
    );
  }
  const stale = state === "on";
  return (
    <div className="pwa-install-card" role="region" aria-label="Notificaciones push">
      <span className="pwa-install-card__text">
        {stale
          ? "Este dispositivo ya no está registrado para recibir avisos. Vuelve a activarlo:"
          : "Avisos en este dispositivo: desactivados"}
      </span>
      <button
        type="button"
        className="btn btn--primary"
        disabled={busy}
        onClick={() => {
          setRequested(true);
          void enable();
        }}
      >
        {stale ? "Volver a activar" : "Activar"}
      </button>
      {error ? <span className="pwa-install-card__steps">{error}</span> : null}
    </div>
  );
}

export default function MessageTestResultCard({ result }: { result: MessageTestResult }) {
  const app = result.channels.find((item) => item.channel === "app");
  const needsPush = app?.push?.status === "no_subscription";
  return (
    <div className="aviso-prueba-resultado" role="status" aria-label={`Resultado de la prueba de ${result.label}`}>
      <p className="aviso-prueba-resultado__resumen">
        <strong>Prueba enviada solo a ti:</strong> {summarizeMessageTest(result)}
      </p>
      <ul className="aviso-prueba-resultado__lista">
        {result.channels.map((item) => (
          <li key={item.channel} className={`aviso-prueba-resultado__canal is-${item.status}`}>
            <span className="aviso-prueba-resultado__marca" aria-hidden="true">{mark(item.status)}</span>
            <span>
              <strong>{CHANNEL_NAMES[item.channel] ?? item.channel}:</strong> {item.detail}
              {item.channel === "app" && item.push ? ` ${item.push.detail}` : ""}
            </span>
          </li>
        ))}
      </ul>
      {app ? (
        <div className="aviso-prueba-resultado__push">
          {needsPush ? <TestPushHelp /> : null}
          <Link to="/admin/notifications">Ver mis notificaciones</Link>
        </div>
      ) : null}
    </div>
  );
}
